import React from 'react';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useNotifications } from '../contexts/NotificationContext.jsx';

/**
 * NotificationBell component for sidebar with theme-aware styling
 * Shows animated indicator for unread notifications and unread count badge
 * Now uses NotificationContext for real-time updates
 */
const NotificationBell = ({ collapsed = false }) => {
  const { theme } = useTheme();
  const { unreadCount, loading, error } = useNotifications();

  // Bell icon with animation for unread notifications
  const BellIcon = () => (
    <svg 
      className={`w-5 h-5 transition-all duration-300 ${
        unreadCount > 0 
          ? `animate-pulse ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'}`
          : theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
      }`}
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
      />
    </svg>
  );

  // Unread count badge
  const UnreadBadge = () => {
    if (unreadCount === 0) return null;
    
    return (
      <span className={`
        absolute -top-1 -right-1 inline-flex items-center justify-center
        px-1.5 py-0.5 text-xs font-bold leading-none rounded-full
        min-w-[1.25rem] h-5
        ${theme === 'dark' 
          ? 'bg-teal-500 text-white' 
          : 'bg-teal-600 text-white'
        }
        animate-pulse
      `}>
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    );
  };

  return (
    <div className="relative inline-flex items-center">
      {/* Bell Icon */}
      <div className="relative">
        <BellIcon />
        <UnreadBadge />
      </div>
      
      {/* Loading indicator (subtle) */}
      {loading && !error && (
        <div className={`ml-2 w-1 h-1 rounded-full animate-pulse ${
          theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'
        }`} />
      )}
      
      {/* Error indicator (subtle) */}
      {error && (
        <div className={`ml-2 w-1 h-1 rounded-full ${
          theme === 'dark' ? 'bg-red-400' : 'bg-red-500'
        }`} title="Failed to load notification count" />
      )}
    </div>
  );
};

export default NotificationBell;
